/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import Honeybadger from 'honeybadger-js'
import _ from 'underscore'

import CustomersEntryPanel from 'pages/shared/CustomerForm/CustomersEntryPanel'
import CustomerFields from 'pages/shared/CustomerForm/CustomerFields'
import PiiFlags from 'pages/shared/CustomerForm/PiiFlags'
import Form from 'pages/shared/Form'

import { findHomeCustomerValuesForDiagCustomerValues } from 'pages/shared/findHomeCustomerValuesForDiagCustomerValues'

import GA4 from 'helpers/ga4'

import { useServerErrorContext } from 'hooks/ServerErrorContext'
import { useStoreContext } from 'hooks/StoreContext'

import useNotificationRecipients from 'hooks/useNotificationRecipients'
import useScreenSize from 'hooks/useScreenSize'

import CustomerModel from 'models/Customer'

import { screenSize } from 'styles/shared'
import { Customer } from 'types/Customer'

type propTypes = {
  customer: Customer
  dealerId: string
  readOnly: boolean
  readOnlyMessage: string
  title: string
  displayDeleteButton: boolean
}

const CustomerForm = ({ customer, dealerId, readOnly, readOnlyMessage, title, displayDeleteButton = false }: propTypes) => {
  const navigate = useNavigate()
  const screenSize = useScreenSize()
  const customerModel = new CustomerModel()

  useNotificationRecipients()

  const { setServerError } = useServerErrorContext()
  const { notificationRecipients, areNotificationRecipientsLoaded } = useStoreContext()

  const selectedNotificationRecipientIds = customer?.notificationRecipientIds
  const allNotificationRecipientIds = _.map(notificationRecipients, (recipient) => recipient.id)

  const [customerAttributes, setCustomerAttributes] = useState({ ...customer })
  const [originalAttributes, setOriginalAttributes] = useState({ ...customer })
  const [errors, setErrors] = useState({ firstName: '', lastName: '', zip: '', email: '', phone: '' })
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const onClickCopy = useCallback(() => {
    const attributesToCopy = findHomeCustomerValuesForDiagCustomerValues(customerAttributes)

    setCustomerAttributes({ ...customerAttributes, ...attributesToCopy })
  }, [setCustomerAttributes, customerAttributes])

  useEffect(() => {
    if (!areNotificationRecipientsLoaded || (customer.id)) { return }
    if (customer) {
      const loadedCustomerAttributes = {
        ...customerAttributes,
        ...customer,
        ...{ notificationRecipientIds: customer && 'notificationRecipientIds' in customer ? selectedNotificationRecipientIds : allNotificationRecipientIds }
      }
      setCustomerAttributes(loadedCustomerAttributes)
      setOriginalAttributes(loadedCustomerAttributes)
    }
  }, [customer, areNotificationRecipientsLoaded])

  const onClickCancel = useCallback(() => {
    const hasChanges = !_.isEqual(customerAttributes, originalAttributes)
    const confirmed = !hasChanges || (hasChanges && confirm('Are you sure you want to cancel?'))

    if (confirmed) {
      navigate(customer.id ? `/customers/${customer.id}` : '/customers')

      const eventData = { category: `${title} Page`, action: 'Click Cancel - Changes Discarded' }

      GA4.sendEvent(eventData)
    }
  }, [customerAttributes, originalAttributes, navigate, customer, title])

  const isNewAndUnacknowledged = customerModel.isNewAndUnacknowledged(customer)
  const isUpdatedAndUnacknowledged = customerModel.isUpdatedAndUnacknowledged(customer)
  const isDuplicateAndUnacknowledged = customerModel.isDuplicateAndUnacknowledged(customer)
  const isUpdateAcknowledged = customerModel.isUpdateAcknowledged(customerAttributes)

  const onSaveCallback = useCallback(() => {
    if (readOnly) {
      alert(readOnlyMessage)
      return
    }
    setIsSaving(true)

    const labelSelectedToRemove = customerModel.getLabelSelectedToRemove(customer, customerAttributes)

    if (labelSelectedToRemove) {
      const eventData = {
        category: `${title} Page`,
        action: `Select ${labelSelectedToRemove} Customer acknowledge box and Click Save`
      }

      GA4.sendEvent(eventData)
    }

    const onSaveSuccessCallback = (res) => {
      navigate(`/customers/${res.id}`)
    }

    const onValidationErrorCallback = (errors) => {
      setErrors(errors)
      setIsSaving(false)
    }

    const onSaveFailureCallBack = (e) => {
      Honeybadger.notify('Error saving in createCustomerCallback', { context: e })
      setServerError('Failed to save customer')
      setIsSaving(false)
    }

    customerModel.save(dealerId, customer.id, customerAttributes, onSaveSuccessCallback, onSaveFailureCallBack, onValidationErrorCallback)
  }, [customer, customerAttributes, dealerId, navigate, readOnly, readOnlyMessage, setServerError, title])

  const onDeleteCallback = useCallback(() => {
    if (readOnly) {
      alert('Read-only view: can\'t delete customer')

      return
    }
    if (confirm('Are you sure you want to delete this customer? This cannot be undone.')) {
      setIsDeleting(true)
      const onDeleteSuccessCallback = () => {
        navigate('/customers')
      }

      const onDeleteFailureCallback = (e) => {
        Honeybadger.notify('Error destroying in deleteCustomerCallback', { context: e })

        setServerError('Failed to delete customer')
        setIsDeleting(false)
      }

      customerModel.delete(dealerId, customer.id, onDeleteSuccessCallback, onDeleteFailureCallback)
      const eventData = { category: `${title} Page`, action: 'Click Delete - Confirmed' }

      GA4.sendEvent(eventData)
    }
  }, [readOnly, dealerId, customer.id, title, navigate, setServerError])

  const onChangeCallback = (updatedAttribute) => {
    setCustomerAttributes({ ...customerAttributes, ...updatedAttribute })
  }

  const isPiiCustomerUnacknowledged = isNewAndUnacknowledged || isUpdatedAndUnacknowledged || isDuplicateAndUnacknowledged

  const ShowCustomerEntryPanel = () => {
    return (
      <CustomersEntryPanel {...{ ...customerAttributes, onClickCopy }} />
    )
  }

  return (
    <S.Container>
      <Form
        title={title}
        onSaveCallback={onSaveCallback}
        onCancelCallback={onClickCancel}
        onDeleteCallback={onDeleteCallback}
        isSaving={isSaving}
        isDeleting={isDeleting}
        displayDeleteButton={displayDeleteButton}
        deleteButtonText="Customer"
      >
        {(screenSize === 'medium' || screenSize === 'small') && isPiiCustomerUnacknowledged && (
          <ShowCustomerEntryPanel />
        )}
        <S.Fieldset>
          <PiiFlags
            isNewAndUnacknowledged={isNewAndUnacknowledged}
            isUpdatedAndUnacknowledged={isUpdatedAndUnacknowledged}
            isDuplicateAndUnacknowledged={isDuplicateAndUnacknowledged}
            isUpdateAcknowledged={isUpdateAcknowledged}
            piiHomeDuplicateNames={customer && customer.piiHomeDuplicateNames}
            piiAcknowledgedAt={customerAttributes.piiAcknowledgedAt}
            piiDuplicateAck={customerAttributes.piiDuplicateAck}
            orginalPiiAcknowledgedAt={originalAttributes.piiAcknowledgedAt}
            onChangeCallback={onChangeCallback}
          />

          <CustomerFields {...{ ...customerAttributes, errors, onChangeCallback }} />
        </S.Fieldset>
      </Form>

      {screenSize === 'large' && isPiiCustomerUnacknowledged && (
        <ShowCustomerEntryPanel />
      )}
    </S.Container>
  )
}

export default CustomerForm

const S = {
  Container: styled.div`
    display: flex;
  `,
  Row: styled.div`
    > :not(:last-child) {
      padding-right: 10px;
    }

    @media ${screenSize.small} {
      > :not(:last-child) {
        padding-right: 0px;
      }
    }
  `,
  Fieldset: styled.fieldset`
    padding: 10px 15px 15px 15px;
    border: none;

    @media ${screenSize.small} {
      padding: 0 5px;
    }
  `,
  Explanation: styled.p`
    margin-top: 13px;
  `
}
